<script setup lang="ts">
import { onMounted, useSlots, type PropType } from 'vue'
import { useFocusUserInputRef } from '../../composables/useInputHelpers'
import { analyticsNameProp, validationProp } from '../common/props'
import OenUiInputWrapper from '../OenUiInputWrapper/OenUiInputWrapper.vue'
import OenUiSpinner from '../OenUiSpinner/OenUiSpinner.vue'
import type { InputType } from './types'
import { inputTypes } from './variants'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    required: true,
    default: '',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  ...validationProp,
  helperText: {
    type: String,
    default: '',
  },
  type: {
    type: String as PropType<InputType>,
    default: 'text',
    validator: (type: InputType) => inputTypes.includes(type),
  },
  focused: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    required: false,
  },
  inlineLabel: {
    type: Boolean,
    default: false,
  },
  ...analyticsNameProp,
})

const slots = useSlots()

const emits = defineEmits(["update:modelValue", "focus", "blur"])

const { userInputRef, focus } = useFocusUserInputRef()

onMounted(() => {
  if (props.focused) {
    focus()
  }
})

const onInput = (payload: Event): void => {
  const target = payload.target as HTMLInputElement
  emits('update:modelValue', target?.value)
}

defineExpose({
  focus,
  userInputRef,
})

defineOptions({
  inheritAttrs: false,
})
</script>

<template>
  <OenUiInputWrapper
    :class="{
      'oen-ui-input--with-left-icon': slots.leftIcon,
      'oen-ui-input--with-right-icon': slots.rightIcon || loading,
    }"
  >
    <template #labelRight>
      <slot name="labelRight"></slot>
    </template>
    <template #default="{ input }">
      <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
      <input
        ref="userInputRef"
        :value="input.modelValue"
        :class="input.classes"
        :type="type"
        v-bind="input.attrs"
        @input="onInput"
        @focus="emits('focus', $event)"
        @blur="emits('blur', $event)"
      />
      <div v-if="slots.leftIcon" class="oen-ui-input-left-icon">
        <!-- @slot leftIcon: For the left Icon -->
        <slot name="leftIcon" />
      </div>
      <div v-if="slots.rightIcon || loading" class="oen-ui-input-right-icon">
        <!-- @slot rightIcon: For the right Icon -->
        <slot name="rightIcon" />
        <!-- Only show loading when rightIcon slot is not in use -->
        <OenUiSpinner
          v-if="!slots.rightIcon"
          :appear="true"
          :size="1.5"
          :loading="loading"
        ></OenUiSpinner>
      </div>
    </template>
  </OenUiInputWrapper>
</template>
